import type { AuditedCourseExpanded } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import posthog from "posthog-js";
import { useState } from "react";

import useAccess from "./useAccess";
import useAuditedCourses from "./useAuditedCourses";
import useFreeCourseClaimModal from "./useFreeCourseClaimModal";
import { type FreeCourseState, getFreeCourseState } from "~/lib/free-course";
import fetcher from "~/utils/fetcher.client";

/**
 * Free-course state for one course page, plus the claim action.
 *
 * @param {string} courseId - The course on this page
 * @returns The state to render, and actions
 */
const useFreeCourse = (
	courseId: string,
): {
	state: FreeCourseState;
	isClaiming: boolean;
	claim: () => Promise<void>;
	/** Opens the claim prompt instead of the membership modal. Returns false when there is no offer. */
	openClaimIfOffered: () => boolean;
} => {
	const { isSignedIn, isLoaded } = useAuth();
	const { isMember, isFormerMember, isPaused, isLoadingAccess } = useAccess();
	const { auditedCourses, isLoadingAuditedCourses, mutate } =
		useAuditedCourses();
	const openClaimModal = useFreeCourseClaimModal((store) => store.open);
	const [isClaiming, setIsClaiming] = useState(false);

	const state = getFreeCourseState({
		isSignedIn: !!isSignedIn,
		isLoading: !isLoaded || isLoadingAccess || isLoadingAuditedCourses,
		isMember,
		isFormerMember,
		isPaused: !!isPaused,
		courseId,
		audits: auditedCourses,
	});

	const claim = async () => {
		setIsClaiming(true);
		try {
			const claimed = await fetcher<AuditedCourseExpanded>("/courses/audited", {
				method: "POST",
				data: { courseId },
			});
			await mutate((current) => [
				...(current ?? []).filter((audit) => audit.id !== claimed.id),
				claimed,
			]);
			posthog.capture("free_course:claimed", {
				course_id: courseId,
			});
		} finally {
			setIsClaiming(false);
		}
	};

	const openClaimIfOffered = () => {
		if (state.kind !== "offer") return false;
		openClaimModal();
		return true;
	};

	return { state, isClaiming, claim, openClaimIfOffered };
};

export default useFreeCourse;
